// v2 owl — leans into the owl silhouette: a tall upright body with a pale
// streaked breast, a big head fronted by a true flat facial disc with amber eyes
// and a hooked beak, long ear tufts, folded wing slabs down the sides, a short
// fanned tail and little perched feet. Two plumages: tawny and grey. Ported 1:1
// from age-of-mcp's v2/owl.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const PLUMAGES = [
  { body: "#8a6a48", breast: "#c2a87e", head: "#927150", disc: "#d8c4a0", wing: "#6e5439", tuft: "#5e4730" },
  { body: "#7a7468", breast: "#b8b2a2", head: "#847e70", disc: "#d2ccba", wing: "#615c50", tuft: "#524e42" },
];

function owlParts(v: number): Part[] {
  const c = PLUMAGES[v % PLUMAGES.length]!;
  const parts: Part[] = [
    // tall upright body with a pale breast
    { geometry: new THREE.IcosahedronGeometry(0.035, 0), color: c.body,
      position: [0, 0.045, 0], scale: [1.05, 1.35, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.breast,
      position: [0.011, 0.04, 0], scale: [0.9, 1.2, 0.9] },
    // big head with the flat facial disc
    { geometry: new THREE.IcosahedronGeometry(0.028, 0), color: c.head,
      position: [0.004, 0.1, 0], scale: [1.05, 0.95, 1.05] },
    { geometry: new THREE.CylinderGeometry(0.0235, 0.0235, 0.008, 8), color: c.disc,
      position: [0.026, 0.1, 0], rotation: [0, 0, Math.PI / 2] },
    // amber eyes and a small hooked beak
    { geometry: new THREE.IcosahedronGeometry(0.0065, 0), color: "#e8b83e",
      position: [0.0315, 0.106, 0.0115] },
    { geometry: new THREE.IcosahedronGeometry(0.0065, 0), color: "#e8b83e",
      position: [0.0315, 0.106, -0.0115] },
    { geometry: new THREE.ConeGeometry(0.005, 0.014, 4), color: "#3a2c1c",
      position: [0.034, 0.092, 0], rotation: [0, 0, -Math.PI / 2] },
    // long ear tufts
    { geometry: new THREE.ConeGeometry(0.0075, 0.022, 4), color: c.tuft,
      position: [0.007, 0.133, 0.018], rotation: [0.35, 0, 0] },
    { geometry: new THREE.ConeGeometry(0.0075, 0.022, 4), color: c.tuft,
      position: [0.007, 0.133, -0.018], rotation: [-0.35, 0, 0] },
    // folded wing slabs down the sides
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.wing,
      position: [-0.011, 0.055, 0.033], scale: [1.0, 1.7, 0.45], rotation: [0, 0, 0.1] },
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.wing,
      position: [-0.011, 0.055, -0.033], scale: [1.0, 1.7, 0.45], rotation: [0, 0, 0.1] },
    // short fanned tail
    { geometry: new THREE.BoxGeometry(0.032, 0.008, 0.024), color: c.wing,
      position: [-0.026, 0.018, 0], rotation: [0, 0, 0.5] },
    // perched feet
    { geometry: new THREE.BoxGeometry(0.013, 0.006, 0.008), color: "#caa45e",
      position: [0.008, 0.003, 0.011] },
    { geometry: new THREE.BoxGeometry(0.013, 0.006, 0.008), color: "#caa45e",
      position: [0.008, 0.003, -0.011] },
  ];
  return parts;
}

export const owlV2 = defineModel({
  id: "owl-v2",
  batchId: "fable",
  name: "Owl (v2)",
  tags: ["creature","owl","low-poly","age-of-mcp","fable"],
  summary: "Low-poly owl.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.07,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => owlParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => owlParts(1) },
  ],
  animations: [],
});
